import { useState, useEffect, useCallback } from 'react';
import { networkService } from '@/services/network.service';

interface ServiceWorkerState {
  isUpdateAvailable: boolean;
  isOffline: boolean;
  registration: ServiceWorkerRegistration | null;
}

export function useServiceWorker() {
  const [state, setState] = useState<ServiceWorkerState>({
    isUpdateAvailable: false,
    isOffline: !networkService.isOnline(),
    registration: null,
  });

  // Check for updates
  const checkForUpdates = useCallback(async () => {
    if (state.registration) {
      try {
        await state.registration.update();
      } catch (error) {
        console.error('Failed to check for SW updates:', error);
      }
    }
  }, [state.registration]);

  // Apply update (refresh the page)
  const applyUpdate = useCallback(() => {
    if (state.registration?.waiting) {
      // Tell the waiting service worker to skip waiting.
      // The 'controllerchange' listener below will reload the page
      // once the new SW has actually taken control.
      state.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    } else {
      // No waiting worker (edge case) — just reload
      window.location.reload();
    }
  }, [state.registration]);

  // Dismiss the update notification
  const dismissUpdate = useCallback(() => {
    setState((prev) => ({ ...prev, isUpdateAvailable: false }));
  }, []);

  useEffect(() => {
    // Handle online/offline status
    const removeNetworkListener = networkService.addListener((connected) => {
      setState((prev) => ({ ...prev, isOffline: !connected }));
    });

    // Register service worker update listener
    let controllerChangeHandler: (() => void) | null = null;

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        setState((prev) => ({ ...prev, registration }));

        // Check if there's already a waiting service worker
        if (registration.waiting) {
          setState((prev) => ({ ...prev, isUpdateAvailable: true }));
        }

        // Listen for new service workers
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New version available
                setState((prev) => ({ ...prev, isUpdateAvailable: true }));
              }
            });
          }
        });
      });

      // Listen for controller change (new SW activated)
      controllerChangeHandler = () => {
        // New service worker has taken control, reload is needed
        window.location.reload();
      };
      navigator.serviceWorker.addEventListener('controllerchange', controllerChangeHandler);
    }

    return () => {
      removeNetworkListener();
      if (controllerChangeHandler) {
        navigator.serviceWorker.removeEventListener('controllerchange', controllerChangeHandler);
      }
    };
  }, []);

  return {
    ...state,
    checkForUpdates,
    applyUpdate,
    dismissUpdate,
  };
}

export default useServiceWorker;
